import * as apiClient from '../api/apiClient';
import { buildPagedQuery } from '../api/pagedQuery';
import { pageInMemoryList } from '../api/localPagedList';
import { unwrapPagedResponseModel, unwrapResponseModel } from '../api/responseModel';

async function requestAndUnwrap(requestPromise) {
  const { data } = await requestPromise;
  return unwrapResponseModel(data);
}

function createPagedList(indexPath) {
  return (query = { page: 1, pageSize: 10 }) => {
    const qs = buildPagedQuery(query);
    return apiClient.get(`${indexPath}?${qs}`).then(({ data }) => unwrapPagedResponseModel(data));
  };
}

function createActaCursadaResource() {
  const listPaged = createPagedList('/api/acur/Index');
  return {
    resetForTests() {
      // Sin caché deduplicada tras F4-011.
    },
    list(query) {
      return listPaged(query);
    },
    lookup(query) {
      return listPaged({ pageSize: 5, ...query });
    },
    getDetalle(id) {
      return requestAndUnwrap(apiClient.get(`/api/acur/detalle/${id}`));
    },
    imprimir(id) {
      return requestAndUnwrap(apiClient.get(`/api/acur/print/${id}`));
    },
    cerrar(id) {
      return requestAndUnwrap(apiClient.get(`/api/acur/cerrar/${id}`));
    },
    saveDetalles(payload) {
      return requestAndUnwrap(apiClient.post('/api/acur', payload));
    }
  };
}

function createActaExamenResource() {
  const listPaged = createPagedList('/api/aex/Index');
  return {
    resetForTests() {
      // Sin caché deduplicada tras F4-011.
    },
    list(query) {
      return listPaged(query);
    },
    lookup(query) {
      return listPaged({ pageSize: 5, ...query });
    },
    getDetalle(id) {
      return requestAndUnwrap(apiClient.get(`/api/aex/detalle/${id}`));
    },
    imprimir(id) {
      return requestAndUnwrap(apiClient.get(`/api/aex/print/${id}`));
    },
    cerrar(id) {
      return requestAndUnwrap(apiClient.get(`/api/aex/cerrar/${id}`));
    },
    consultar(query = { page: 1, pageSize: 10, filters: {} }) {
      const { filters = {}, ...paging } = query;
      const params = new URLSearchParams(buildPagedQuery(paging));
      Object.entries(filters).forEach(([key, value]) => {
        if (value != null && value !== '') {
          params.set(key, String(value));
        }
      });
      return apiClient.get(`/api/aex/consultar?${params}`).then(({ data }) => unwrapPagedResponseModel(data));
    },
    saveEncabezado(payload) {
      return requestAndUnwrap(apiClient.post('/api/aex/encabezado', payload));
    },
    saveNotas(payload) {
      return requestAndUnwrap(apiClient.post('/api/aex', payload));
    }
  };
}

const actaCursada = createActaCursadaResource();
const actaExamen = createActaExamenResource();
const detalleCursadaCache = new Map();
const detalleExamenCache = new Map();

export function resetActasCursadaListCacheForTests() {
  actaCursada.resetForTests();
  detalleCursadaCache.clear();
}

export function resetActasExamenListCacheForTests() {
  actaExamen.resetForTests();
  detalleExamenCache.clear();
}

export function listActasCursada(query) {
  return actaCursada.list(query);
}

export function lookupActasCursada(query) {
  return actaCursada.lookup(query);
}

export function getActaCursadaDetalle(id) {
  return actaCursada.getDetalle(id);
}

export async function listActaCursadaDetalle(actaId, query = { page: 1, pageSize: 10 }) {
  if (!detalleCursadaCache.has(actaId)) {
    const items = await actaCursada.getDetalle(actaId);
    detalleCursadaCache.set(actaId, items || []);
  }
  return pageInMemoryList(detalleCursadaCache.get(actaId), query);
}

export function clearActaCursadaDetalleCache(actaId) {
  detalleCursadaCache.delete(actaId);
}

export function seedActaCursadaDetalleCache(actaId, items) {
  detalleCursadaCache.set(actaId, items || []);
}

export function imprimirActaCursada(id) {
  return actaCursada.imprimir(id);
}

export function cerrarActaCursada(id) {
  return actaCursada.cerrar(id);
}

export function saveActaCursada(payload) {
  return actaCursada.saveDetalles(payload);
}

export function listActasExamen(query) {
  return actaExamen.list(query);
}

export function lookupActasExamen(query) {
  return actaExamen.lookup(query);
}

export function getActaExamenDetalle(id) {
  return actaExamen.getDetalle(id);
}

export async function listActaExamenDetalle(actaId, query = { page: 1, pageSize: 10 }) {
  if (!detalleExamenCache.has(actaId)) {
    const items = await actaExamen.getDetalle(actaId);
    detalleExamenCache.set(actaId, items || []);
  }
  return pageInMemoryList(detalleExamenCache.get(actaId), query);
}

export function clearActaExamenDetalleCache(actaId) {
  detalleExamenCache.delete(actaId);
}

export function seedActaExamenDetalleCache(actaId, items) {
  detalleExamenCache.set(actaId, items || []);
}

export function imprimirActaExamen(id) {
  return actaExamen.imprimir(id);
}

export function cerrarActaExamen(id) {
  return actaExamen.cerrar(id);
}

export function consultarActasExamen(query) {
  return actaExamen.consultar(query);
}

export function saveActaExamenEncabezado(payload) {
  return actaExamen.saveEncabezado(payload);
}

export function saveActaExamen(payload) {
  return actaExamen.saveNotas(payload);
}
